import React, { useState, useEffect } from 'react';
import { useToast } from '../../context/ToastContext';
import { Card, Button, Badge, Modal, SectionHeader, Spinner } from '../../components/shared/UI';
import API from '../../utils/apiClient';

const LEVELS = ['A1','A2','B1','B2'];

export default function AssignmentsPage() {
  const { addToast } = useToast();

  const [grouped, setGrouped]   = useState([]);
  const [allAssign, setAllAssign] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [filterLevel, setFilterLevel] = useState('');

  const [addModal, setAddModal] = useState(false);
  const [form, setForm]         = useState({ teacherId:'', studentId:'', level:'A1' });
  const [saving, setSaving]     = useState(false);
  const [removing, setRemoving] = useState(null);

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [aRes, tRes, sRes] = await Promise.all([
        API.get('/super-admin/assignments'),
        API.get('/super-admin/teachers'),
        API.get('/super-admin/students'),
      ]);
      setAllAssign(aRes.data.assignments || []);
      setGrouped(aRes.data.grouped || []);
      setTeachers(tRes.data.teachers || []);
      setStudents(sRes.data.students || []);
    } catch { addToast('Failed to load assignments','error'); }
    finally { setLoading(false); }
  };

  const createAssignment = async () => {
    if (!form.teacherId || !form.studentId || !form.level) {
      addToast('All fields required','error'); return;
    }
    setSaving(true);
    try {
      await API.post('/super-admin/assignments', form);
      addToast('Student assigned successfully!','success');
      setAddModal(false);
      setForm({ teacherId:'', studentId:'', level:'A1' });
      loadAll();
    } catch (err) { addToast(err.message||'Failed','error'); }
    finally { setSaving(false); }
  };

  const removeAssignment = async (id) => {
    setRemoving(id);
    try {
      await API.delete(`/super-admin/assignments/${id}`);
      addToast('Assignment removed','success');
      loadAll();
    } catch (err) { addToast(err.message||'Failed','error'); }
    finally { setRemoving(null); }
  };

  // Filter assignments by level
  const filtered = filterLevel
    ? allAssign.filter(a => a.level === filterLevel)
    : allAssign;

  // Students filtered by the teacher's assigned levels in the form
  const selectedTeacher = teachers.find(t => String(t.id) === String(form.teacherId));
  const teacherLevels   = selectedTeacher?.assigned_levels || LEVELS;
  const eligibleStudents = students.filter(s =>
    teacherLevels.includes(s.current_level) || teacherLevels.includes(form.level)
  );

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-de-black">Teacher–Student Assignments</h1>
          <p className="text-sm text-gray-500 mt-1">
            Assign students to teachers. Teachers only see their assigned students.
          </p>
        </div>
        <Button variant="primary" onClick={() => setAddModal(true)}>+ Assign Student</Button>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {LEVELS.map(l => {
          const count = allAssign.filter(a => a.level === l).length;
          return (
            <button key={l} onClick={() => setFilterLevel(filterLevel === l ? '' : l)}
              className={`p-4 rounded-xl border-2 text-left transition-all
                ${filterLevel === l ? 'border-brand-500 bg-brand-50' : 'border-gray-200 bg-white hover:border-brand-300'}`}>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Level {l}</p>
              <p className="text-2xl font-bold text-de-black mt-1">{count}</p>
              <p className="text-xs text-gray-400">assignment{count !== 1 ? 's' : ''}</p>
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Spinner size="lg" className="text-brand-500" /></div>
      ) : (
        <>
          {/* Filter indicator */}
          {filterLevel && (
            <div className="flex items-center gap-3 mb-4 p-3 bg-brand-50 rounded-xl border border-brand-200">
              <span className="text-sm font-semibold text-brand-700">Filtering: Level {filterLevel}</span>
              <button onClick={() => setFilterLevel('')} className="text-xs text-brand-500 hover:text-brand-700 font-medium underline">Clear filter</button>
              <span className="text-xs text-gray-500 ml-auto">{filtered.length} assignment{filtered.length !== 1 ? 's' : ''}</span>
            </div>
          )}

          {/* Assignments table */}
          {filtered.length === 0 ? (
            <Card className="p-12 text-center">
              <p className="text-4xl mb-3">📋</p>
              <p className="text-base font-bold text-de-black mb-1">No assignments yet</p>
              <p className="text-sm text-gray-500 mb-4">
                {filterLevel ? `No Level ${filterLevel} assignments found.` : 'Start by assigning students to teachers.'}
              </p>
              <Button variant="primary" onClick={() => setAddModal(true)}>+ Assign Student</Button>
            </Card>
          ) : (
            <Card className="overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-surface border-b border-gray-200">
                    <tr>
                      {['Student','Level','Teacher','Assigned','Actions'].map(h => (
                        <th key={h} className="text-left py-3 px-4 text-xs font-bold text-gray-500 uppercase tracking-wide">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map(a => (
                      <tr key={a.id} className="border-b border-gray-50 hover:bg-surface transition-colors">
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <div className="w-7 h-7 rounded-full bg-brand-500 text-white flex items-center justify-center text-xs font-bold flex-shrink-0">
                              {a.student_name?.charAt(0)||'?'}
                            </div>
                            <div>
                              <p className="font-semibold text-de-black text-xs">{a.student_name}</p>
                              <p className="text-xs text-gray-400">{a.student_email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          {/* Gold badge for level */}
                          <Badge variant="gold">{a.level}</Badge>
                        </td>
                        <td className="py-3 px-4">
                          <p className="font-medium text-de-black text-xs">{a.teacher_name}</p>
                          <p className="text-xs text-gray-400">{a.teacher_email}</p>
                        </td>
                        <td className="py-3 px-4 text-xs text-gray-500">
                          {new Date(a.assigned_at).toLocaleDateString('en-GB', { day:'numeric', month:'short', year:'numeric' })}
                        </td>
                        <td className="py-3 px-4">
                          {/* Red danger button */}
                          <Button size="sm" variant="danger"
                            loading={removing === a.id}
                            onClick={() => removeAssignment(a.id)}>
                            Remove
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}

          {/* Grouped by teacher view */}
          {!filterLevel && grouped.length > 0 && (
            <div className="mt-8">
              <h2 className="text-lg font-bold text-de-black mb-4">By Teacher</h2>
              <div className="space-y-4">
                {grouped.map(group => (
                  <Card key={group.teacherId} className="p-5" accent="red">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 rounded-full bg-de-black text-white flex items-center justify-center font-bold">
                        {group.teacherName?.charAt(0)||'T'}
                      </div>
                      <div>
                        <p className="font-bold text-de-black">{group.teacherName}</p>
                        <p className="text-xs text-gray-400">{group.teacherEmail} · {group.assignments.length} student{group.assignments.length !== 1 ? 's' : ''}</p>
                      </div>
                    </div>
                    <div className="space-y-2">
                      {LEVELS.filter(l => group.assignments.some(a => a.level === l)).map(level => (
                        <div key={level}>
                          <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-1.5">Level {level}</p>
                          <div className="flex flex-wrap gap-2">
                            {group.assignments.filter(a => a.level === level).map(a => (
                              <div key={a.id} className="flex items-center gap-1.5 px-3 py-1.5 bg-surface rounded-xl border border-gray-200 text-xs">
                                <span className="font-medium text-de-black">{a.studentName}</span>
                                <button onClick={() => removeAssignment(a.id)}
                                  className="text-gray-400 hover:text-brand-500 font-bold ml-1 transition-colors">×</button>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* Add Assignment Modal */}
      <Modal isOpen={addModal} onClose={() => setAddModal(false)} title="Assign Student to Teacher" size="sm">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-bold text-de-black mb-1.5">Teacher</label>
            <select value={form.teacherId} onChange={e => setForm(p => ({...p, teacherId:e.target.value, studentId:''}))}
              className="w-full px-3 py-2 text-sm border-2 border-gray-200 rounded-xl focus:border-brand-500 focus:outline-none bg-white">
              <option value="">— Select a teacher —</option>
              {teachers.filter(t => t.is_active).map(t => (
                <option key={t.id} value={t.id}>{t.name} ({(t.assigned_levels||[]).join(', ')})</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-bold text-de-black mb-1.5">Level</label>
            <div className="flex gap-2">
              {LEVELS.map(l => (
                <label key={l}
                  className={`flex-1 py-2 text-center rounded-lg border-2 cursor-pointer text-sm font-semibold transition-all
                    ${form.level === l ? 'border-brand-500 bg-brand-50 text-brand-700' : 'border-gray-200 text-gray-500 hover:border-gray-300'}`}>
                  <input type="radio" name="level" value={l} checked={form.level === l}
                    onChange={() => setForm(p => ({...p, level:l}))} className="sr-only" />
                  {l}
                </label>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-bold text-de-black mb-1.5">Student</label>
            <select value={form.studentId} onChange={e => setForm(p => ({...p, studentId:e.target.value}))}
              disabled={!form.teacherId}
              className="w-full px-3 py-2 text-sm border-2 border-gray-200 rounded-xl focus:border-brand-500 focus:outline-none bg-white disabled:opacity-50">
              <option value="">— Select a student —</option>
              {students.map(s => (
                <option key={s.student_id} value={s.student_id}>
                  {s.name} (Level {s.current_level})
                </option>
              ))}
            </select>
            {!form.teacherId && (
              <p className="text-xs text-gray-400 mt-1">Select a teacher first.</p>
            )}
          </div>

          <div className="flex gap-3 justify-end pt-2">
            <Button variant="ghost" onClick={() => setAddModal(false)}>Cancel</Button>
            {/* Primary = red */}
            <Button variant="primary" loading={saving} onClick={createAssignment}>Assign Student</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
